import { Listify, PickNonNullable } from "../helpers/types";
import { PaginationFilters } from "./Pagination";
import { SearchQuery } from "./Search";

export const GST_PERCENTAGE = 10;

export type VehicleMake = {
  id: string;
  name: string;
};

export type VehicleModel = {
  id: string;
  name: string;
  makeId: string;
};

export type VehicleModelYear = string;

export type FuelType = "electric" | "petrol" | "diesel" | "gas" | "unmapped";

export const FuelTypeLabelMap: Record<FuelType, string> = {
  electric: "Electric",
  petrol: "Petrol",
  diesel: "Diesel",
  gas: "Gas",
  unmapped: "Not Classified",
};

export type VehicleClass =
  | "suv"
  | "passenger-people-mover"
  | "utility-commercial"
  | "heavy-commercial"
  | "unmapped";

export const VehicleClassLabelMap: Record<VehicleClass, string> = {
  suv: "SUV",
  "passenger-people-mover": "Passenger / People Mover",
  "utility-commercial": "Utility / Commercial",
  "heavy-commercial": "Heavy Commercial",
  unmapped: "Not Classified",
};

export type VehicleSegment =
  | "micro"
  | "small"
  | "medium"
  | "large"
  | "sports"
  | "utility"
  | "people-mover"
  | "commercial"
  | "van"
  | "unmapped";

export const VehicleSegmentLabelMap: Record<VehicleSegment, string> = {
  micro: "Micro",
  small: "Small",
  medium: "Medium",
  large: "Large",
  sports: "Sports",
  utility: "Utility",
  "people-mover": "People Mover",
  commercial: "Commercial",
  van: "Van",
  unmapped: "Not Classified",
};

export type VehicleClassSegmentCascadeMap = Record<
  VehicleClass,
  VehicleSegment[]
>;
export const dummyVehicleClassSegmentCascadeMap: Omit<
  VehicleClassSegmentCascadeMap,
  "unmapped"
> = {
  suv: ["large", "small", "medium", "unmapped"],
  "passenger-people-mover": [
    "large",
    "small",
    "medium",
    "micro",
    "people-mover",
    "sports",
    "unmapped",
  ],
  "utility-commercial": ["commercial", "utility", "van", "unmapped"],
  "heavy-commercial": ["commercial", "unmapped"],
};

export type DriveDescription =
  | "front-wheel-drive"
  | "rear-wheel-drive"
  | "four-wheel-drive"
  | "4x4-on-demand"
  | "4x4-dual-range"
  | "4x4-constant"
  | "4x4"
  | "unmapped";

export const DriveDescriptionLabelMap: Record<DriveDescription, string> = {
  "front-wheel-drive": "Front Wheel Drive",
  "rear-wheel-drive": "Rear Wheel Drive",
  "four-wheel-drive": "Four Wheel Drive",
  "4x4-on-demand": "4x4 On Demand",
  "4x4-dual-range": "4x4 Dual Range",
  "4x4-constant": "4x4 Constant",
  "4x4": "4x4",
  unmapped: "Not Classified",
};

export type Transmission = "automatic" | "manual" | "unmapped";

export const TransmissionLabelMap: Record<Transmission, string> = {
  automatic: "Automatic",
  manual: "Manual",
  unmapped: "Not Classified",
};

export type BodyType =
  | "suv"
  | "van-wagon"
  | "van"
  | "utility"
  | "sedan"
  | "hatchback"
  | "coupe"
  | "convertible"
  | "bus"
  | "unmapped"
  | "wagon";

export const BodyTypeLabelMap: Record<BodyType, string> = {
  suv: "SUV",
  "van-wagon": "Van Wagon",
  van: "Van",
  utility: "Ute",
  sedan: "Sedan",
  hatchback: "Hatchback",
  coupe: "Coupe",
  convertible: "Convertible",
  bus: "Bus",
  unmapped: "Not Classified",
  wagon: "Wagon",
};

export type PowerPlantType = "bev" | "ice" | "phev" | "fcev" | "hev";

export const PowerPlantLabelMap: Record<string, string> = {
  bev: "Electric",
  ice: "Petrol / Diesel",
  phev: "Plug-in Hybrid",
  fcev: "Fuel Cell",
  hev: "Hybrid",
};

export type VehicleVariant = {
  id: string;
  name: string;
  shortName: string;
  makeId: string;
  modelId: string;
  modelYear: string;
  code: string;
  driveType: string;
  listPriceGross: number;
  powerPlantTypeId: string;
};

export type VehicleVariantPhoto = {
  id: string;
  variantId: string;
  type: "front" | "rear" | "interior" | "side" | "engine";
  url: string;
  code: string;
  description: string;
};

export type RedbookVehiclePhoto = {
  id: number;
  vehicleid: number;
  photoangle: string;
  caption: string;
  sequenceno: number;
  href: string;
};

export type VehicleOptionType = "auto-ux" | "dealer";

export type VehicleOptionCategory = {
  id: string;
  name: string;
  displayOrder: number;
  optionIds: string[];
};

export type VehicleOption = {
  id: string;
  catchEOptionId: string;
  displayOrder: number;
  price: number;
  priceExGst: number;
  name: string;
  description: string | null;
  type: VehicleOptionType;
  productInfoDownloadURL: string | null;
  productInfoDownloadName: string | null;
  imageUrl: string | null;
  available: boolean;
  supplierId: string | null;
  supplierCategory: string | null;
};

export type VehicleOptions = {
  standardEquipment: VehicleStandardEquipment[];
  categories: VehicleOptionCategory[];
  options: {
    dealer: Record<string, VehicleOption>;
    inclusion: Record<string, VehicleOption>;
    manufacturer: Record<string, VehicleOption>;
  };
};

export type VehicleStandardEquipment = {
  catchEStandardEquipmentId: string;
  description: string;
};

export type VehicleMatch = {
  make?: VehicleMake;
  model?: VehicleModel;
  year?: string;
  photo: RedbookVehiclePhoto | null;
};

export type VehicleSuggestions = {
  queryId: string;
  suggestions: VehicleSuggestion[];
};

export type VehicleSuggestion = {
  match: VehicleMatch;
  make: string;
  model: string;
  pros: string;
  cons: string;
  comparisonDetails: string;
  value: number;
  comfort: number;
  performance: number;
};

export type VehicleReviewResponse = {
  queryId: string;
  review: VehicleReview;
};

export type VehicleReview = {
  vehicleVariantId: string;
  redbookCode: string;
  variant: APVehicleVariant;
  comparison: string;
  value: number;
  valueText: string;
  comfort: number;
  comfortText: string;
  performance: number;
  performanceText: string;
  costToRun: number;
  costToRunText: string;
  pros: string;
  cons: string;
};

export const calculateWeeklyVehicleOptionCost = (
  totalIncGst: number,
  savingsPercentage: number,
  term: number
) => {
  const weeksInTerm = (term / 12) * 52;
  const costPerWeek =
    (totalIncGst * (1 - savingsPercentage / 100)) / weeksInTerm;
  return costPerWeek;
};

export type APVehicleVariant = {
  id: string;
  redbookVehicleId: string;
  redbookVehicleCode: string;
  makeId: string;
  modelId: string;
  cemakeId: string | null;
  cemodelId: string | null;
  cevariantId: string | null;
  make: string;
  model: string;
  year: string;
  variant: string;
  listPriceIncGst: number;
  fuelType: FuelType | null;
  segment: VehicleSegment | null;
  class: VehicleClass | null;
  bodyType: BodyType | null;
  towingUnbrakedKg: number | null;
  towingBrakedKg: number | null;
  co2Combined: number | null;
  powerToWeight: number | null;
  driveDescription: DriveDescription | null;
  transmission: Transmission | null;
  doors: number | null;
  regularServiceIntervalMonths: number | null;
  regularServiceIntervalKms: number | null;
  countryOfOrigin: string | null;
  batteryType: string | null;
  rimMaterial: string | null;
  frontRim: string | null;
  rearRim: string | null;
  length: number | null;
  width: number | null;
  height: number | null;
  kerbWeight: number | null;
  warrantyYears: number | null;
  powerKw: number | null;
  torque: number | null;
  seatCapacity: number | null;
  acceleration: number | null;
  range: number | null;
  fuelEconomy: number | null;
  powerPlantType: PowerPlantType | null;
  ancaprating: string | null;
  minCargoVolume: number | null;
  maxCargoVolume: number | null;
  payload: number | null;
  unMappedValues: Partial<
    Record<keyof Omit<APVehicleVariant, "unMappedValues">, string>
  >;
  crmLastSync: string | null;
  primaryVehicleImage: string | null;
  releaseYear: string | null;
  releaseMonth: string | null;
};

export const APVehicleVariantLabelMap: Record<keyof APVehicleVariant, string> =
  {
    id: "",
    redbookVehicleId: "",
    redbookVehicleCode: "",
    makeId: "",
    modelId: "",
    cemakeId: "",
    cemodelId: "",
    cevariantId: "",
    unMappedValues: "",
    crmLastSync: "",
    primaryVehicleImage: "",
    make: "Make",
    model: "Model",
    year: "Year",
    variant: "Variant",
    listPriceIncGst: "List Price",
    fuelType: "Fuel Type",
    segment: "Vehicle Class",
    class: "Class",
    bodyType: "Vehicle Type",
    towingUnbrakedKg: "Towing Unbraked",
    towingBrakedKg: "Towing Braked",
    co2Combined: "CO2 Combined",
    powerToWeight: "Power to Weight",
    regularServiceIntervalMonths: "Regular Service Interval (Mth)",
    regularServiceIntervalKms: "Regular Service Interval (Km)",
    countryOfOrigin: "Country of Origin",
    batteryType: "Battery Type",
    rimMaterial: "Rim Material",
    frontRim: "Front Rim",
    rearRim: "Rear Rim",
    length: "Length",
    width: "Width",
    height: "Height",
    kerbWeight: "Kerb Weight",
    warrantyYears: "Warranty",
    powerKw: "Power",
    torque: "Torque",
    seatCapacity: "Seats",
    acceleration: "0-100 sec",
    range: "Range",
    fuelEconomy: "Fuel Economy (Combined)",
    powerPlantType: "Power Plant",
    ancaprating: "ANCAP",
    minCargoVolume: "Cargo Volume (Min)",
    maxCargoVolume: "Cargo Volume (Max)",
    payload: "Payload",
    driveDescription: "Drive Description",
    transmission: "Transmission",
    doors: "Doors",
    releaseYear: "Release Year",
    releaseMonth: "Release Month",
  };

export const APVehicleAspectUnitMap: Partial<
  Record<keyof APVehicleVariant, string>
> = {
  warrantyYears: "yr",
  powerKw: "kW",
  torque: "Nm",
  minCargoVolume: "L",
  maxCargoVolume: "L",
  fuelEconomy: "L/100km",
  acceleration: "sec",
  range: "km",
  towingUnbrakedKg: "kg",
  towingBrakedKg: "kg",
  co2Combined: "g/km",
  powerToWeight: "kW/t",
  regularServiceIntervalMonths: "mth",
  regularServiceIntervalKms: "km",
  ancaprating: "star",
  width: "mm",
  length: "mm",
  height: "mm",
  kerbWeight: "kg",
  payload: "kg",
};

export const APVehicleAspectValueLabelMap: Partial<
  Record<keyof APVehicleVariant, Record<string, string>>
> = {
  bodyType: BodyTypeLabelMap,
  segment: VehicleSegmentLabelMap,
  class: VehicleClassLabelMap,
  driveDescription: DriveDescriptionLabelMap,
  fuelType: FuelTypeLabelMap,
  powerPlantType: PowerPlantLabelMap,
  transmission: TransmissionLabelMap,
};

export const APVehicleValueFormatterOptionsMap: Partial<
  Record<keyof APVehicleVariant, StandardVehicleValueFormatterOptions>
> = {
  listPriceIncGst: {
    numberFormatter: "dollars",
  },
  regularServiceIntervalKms: {
    numberFormatter: "thousands",
  },
  regularServiceIntervalMonths: {
    numberFormatter: "thousands",
  },
  towingUnbrakedKg: {
    numberFormatter: "thousands",
  },
  towingBrakedKg: {
    numberFormatter: "thousands",
  },
  co2Combined: {
    numberFormatter: "thousands",
  },
  powerToWeight: {
    numberFormatter: "thousands",
  },
  minCargoVolume: {
    numberFormatter: "thousands",
  },
  maxCargoVolume: {
    numberFormatter: "thousands",
  },
  range: {
    numberFormatter: "thousands",
  },
  fuelEconomy: {
    decimalPlaces: 1,
  },
  acceleration: {
    decimalPlaces: 1,
  },
  powerKw: {
    numberFormatter: "thousands",
  },
  torque: {
    numberFormatter: "thousands",
  },
  height: {
    numberFormatter: "thousands",
  },
  width: {
    numberFormatter: "thousands",
  },
  length: {
    numberFormatter: "thousands",
  },
  kerbWeight: {
    numberFormatter: "thousands",
  },
};

// Vehicle Aspects
export type APVehicleAspectOptionMap = PickNonNullable<
  APVehicleVariant,
  | "year"
  | "segment"
  | "class"
  | "bodyType"
  | "driveDescription"
  | "powerPlantType"
  | "fuelType"
  | "make"
  | "seatCapacity"
  | "transmission"
>;
export type APVehicleAspectLimitMap = PickNonNullable<
  APVehicleVariant,
  | "listPriceIncGst"
  | "minCargoVolume"
  | "powerKw"
  | "range"
  | "fuelEconomy"
  | "acceleration"
  | "warrantyYears"
  | "towingUnbrakedKg"
  | "co2Combined"
  | "powerToWeight"
  | "ancaprating"
>;

export type APVehicleAspectOptionKey = keyof APVehicleAspectOptionMap;
export type APVehicleAspectLimitKey = keyof APVehicleAspectLimitMap;

export type APVehicleAspectOption<K extends APVehicleAspectOptionKey> = {
  [P in K]: APVehicleAspectOptionMap[P];
};
export type APVehicleAspectLimit<K extends APVehicleAspectLimitKey> = {
  [P in K]: APVehicleAspectLimitMap[P];
};

export type APVehicleAspectLimits = {
  min: number;
  max: number;
  step: number;
  defaultStart?: number;
  defaultEnd?: number;
};

export type APVehicleAspects = {
  options: Listify<APVehicleAspectOptionMap>;
  limits: Record<keyof APVehicleAspectLimitMap, APVehicleAspectLimits>;
};

export type VehicleVariantSearchFilterInputs = Pick<
  APVehicleVariant,
  | "year"
  | "segment"
  | "class"
  | "bodyType"
  | "driveDescription"
  | "powerPlantType"
  | "fuelType"
  | "make"
  | "listPriceIncGst"
  | "seatCapacity"
  | "minCargoVolume"
  | "powerKw"
  | "range"
  | "fuelEconomy"
  | "acceleration"
  | "warrantyYears"
  | "ancaprating"
  | "towingUnbrakedKg"
  | "co2Combined"
  | "powerToWeight"
  | "transmission"
>;

export type VehicleVariantSearchSortOptions = Pick<
  APVehicleVariant,
  "listPriceIncGst" | "powerKw" | "range" | "minCargoVolume" | "make"
>;

export type VehicleVariantSearchFilters = PaginationFilters &
  SearchQuery<
    VehicleVariantSearchFilterInputs,
    VehicleVariantSearchSortOptions
  >;

export type VehicleComparison = {
  variant: APVehicleVariant;
  review: VehicleReview | null;
};

export type VehicleComparisonItem = {
  redbookCode: string;
  make: string;
  model: string;
  year: string;
  variant: string;
  value: number | null;
  comfort: number | null;
  summary: string;
  performance: number | null;
  runningCosts: number | null;
  imageUrl: string;
  aiReview: string;
  aiSuggestions: string;
  ceMakeId: string | null;
  ceModelId: string | null;
  ceVariantId: string | null;

  "vehicle.price": number;
  "vehicle.doorAndBodyType": string | null;
  "vehicle.seats": number | null;
  "vehicle.ancapRating": number | null;
  "vehicle.warrantYrs": number | null;
  "vehicle.serviceIntervalMths": number | null;
  "vehicle.serviceIntervalKms": number | null;
  "vehicle.countryOfOrigin": string | null;

  "powerPlant.powerKw": number | null;
  "powerPlant.torque": number | null;
  "powerPlant.powerPlantName": string | null;
  "powerPlant.acceleration": number | null;
  "powerPlant.range": number | null;
  "powerPlant.batteryType": string | null;
  "powerPlant.driveDescription": string | null;
  "powerPlant.fuelType": string | null;

  "payload.minCargoVolume": number | null;
  "payload.maxCargoVolume": number | null;
  "payload.towingUnbrakedKg": number | null;
  "payload.towingBrakedKg": number | null;
  "payload.payload": number | null;

  "wheels.rimMaterial": string | null;
  "wheels.frontRim": string | null;
  "wheels.rearRim": string | null;

  "dimensions.length": number | null;
  "dimensions.width": number | null;
  "dimensions.height": number | null;
  "dimensions.kerbWeight": number | null;
};

export const CriteriaLabelMap: Record<keyof VehicleComparisonItem, string> = {
  redbookCode: "",
  make: "",
  model: "",
  year: "",
  variant: "Vehicle",
  imageUrl: "",
  value: "Value^",
  comfort: "Comfort^",
  performance: "Performance^",
  runningCosts: "Running Costs^",
  summary: "Summary",
  aiReview: "Review",
  aiSuggestions: "Alternative Vehicles",
  ceMakeId: "",
  ceModelId: "",
  ceVariantId: "",
  "vehicle.price": APVehicleVariantLabelMap.listPriceIncGst,
  "vehicle.doorAndBodyType": APVehicleVariantLabelMap.bodyType,
  "vehicle.seats": APVehicleVariantLabelMap.seatCapacity,
  "vehicle.ancapRating": APVehicleVariantLabelMap.ancaprating,
  "vehicle.warrantYrs": APVehicleVariantLabelMap.warrantyYears,
  "vehicle.serviceIntervalMths":
    APVehicleVariantLabelMap.regularServiceIntervalMonths,
  "vehicle.serviceIntervalKms":
    APVehicleVariantLabelMap.regularServiceIntervalKms,
  "vehicle.countryOfOrigin": APVehicleVariantLabelMap.countryOfOrigin,
  "powerPlant.powerKw": APVehicleVariantLabelMap.powerKw,
  "powerPlant.torque": APVehicleVariantLabelMap.torque,
  "powerPlant.powerPlantName": APVehicleVariantLabelMap.powerPlantType,
  "powerPlant.acceleration": APVehicleVariantLabelMap.acceleration,
  "powerPlant.range": APVehicleVariantLabelMap.range,
  "powerPlant.batteryType": APVehicleVariantLabelMap.batteryType,
  "powerPlant.driveDescription": APVehicleVariantLabelMap.driveDescription,
  "powerPlant.fuelType": APVehicleVariantLabelMap.fuelType,
  "payload.minCargoVolume": APVehicleVariantLabelMap.minCargoVolume,
  "payload.maxCargoVolume": APVehicleVariantLabelMap.maxCargoVolume,
  "payload.towingUnbrakedKg": APVehicleVariantLabelMap.towingUnbrakedKg,
  "payload.towingBrakedKg": APVehicleVariantLabelMap.towingBrakedKg,
  "payload.payload": APVehicleVariantLabelMap.payload,
  "wheels.rimMaterial": APVehicleVariantLabelMap.rimMaterial,
  "wheels.frontRim": APVehicleVariantLabelMap.frontRim,
  "wheels.rearRim": APVehicleVariantLabelMap.rearRim,
  "dimensions.length": APVehicleVariantLabelMap.length,
  "dimensions.width": APVehicleVariantLabelMap.width,
  "dimensions.height": APVehicleVariantLabelMap.height,
  "dimensions.kerbWeight": APVehicleVariantLabelMap.kerbWeight,
};

export type StandardVehicleValueFormatterOptions = {
  numberFormatter?: "thousands" | "dollars";
  decimalPlaces?: number;
  fallbacks?: Partial<Record<keyof APVehicleVariant, string>>;
};
